import {
  EstimationRecord,
  EstimationRecordsDef,
  EstimationRecordsPage,
  RecordSortBy,
  RecordValueType,
} from "../type/batch-def";

/** 数値でなければ null。`point_estimate` / `observed_value` は片方が null で来る。 */
function toNumber(value: unknown): number | null {
  const num = Number(value);
  return value != null && Number.isFinite(num) ? num : null;
}

export class EstimationRecordsAdapter {
  /**
   * 推定結果の 1 ページ分を画面用に正規化する。
   *
   * 予測値 / 観測値 / 両方の絞り込みは `value_type` で BE に任せる。
   * ページ単位で取得するので、ここで行を落としたり並べ替えたりはしない
   * （ページごとの件数やページをまたいだ順序が崩れるため）。
   *
   * 全件数は BE の `n_results`（条件に合う全件数）を使う。
   * 万一取れなかったときだけ、呼び出し側が渡した件数、それも無ければ
   * 満杯のページなら「次がある」とみなす。
   */
  static toPageDTO(
    data: EstimationRecordsDef,
    { offset, limit, fallbackTotal }: {
      offset: number;
      limit: number;
      fallbackTotal?: number;
    }
  ): EstimationRecordsPage {
    const records: EstimationRecord[] = (data?.items ?? []).map((item) => {
      // uploaded_only は推定値、source_only は実測値。両方入ることはない
      const estimate = toNumber(item.point_estimate);
      const observed = toNumber(item.observed_value);
      const isEstimated = estimate != null;

      return {
        id: item.record_id,
        identifierCode: item.identifier_code,
        presence: item.presence,
        isUploaded: item.presence === "uploaded_only",
        value: isEstimated ? estimate : observed,
        isEstimated,
        // 信頼区間が付くのは推定値のときだけ
        lower: isEstimated ? (item.uncertainty_interval?.lower ?? null) : null,
        upper: isEstimated ? (item.uncertainty_interval?.upper ?? null) : null,
      };
    });

    const reported = toNumber(data?.n_results);
    const total =
      reported ??
      fallbackTotal ??
      offset + records.length + (records.length === limit ? 1 : 0);

    return { records, total };
  }
}

/**
 * `n_results` が取れなかったときに使う件数。推定結果のメタデータから、
 * 表示している種類に対応する件数を選ぶ。
 */
export function fallbackRecordsTotal(
  estimation: {
    uploaded_profile_count?: number;
    fit_source_record_count?: number;
    result_record_count?: number;
  } | null | undefined,
  valueType: "estimated" | "observed" | "both"
): number | undefined {
  if (!estimation) return undefined;
  if (valueType === "estimated") return estimation.uploaded_profile_count;
  if (valueType === "observed") return estimation.fit_source_record_count;
  return estimation.result_record_count;
}

/** 結果表で並べ替えできる列。BE の sort_by に対応するものだけ。 */
export type RecordSortField = "identifierCode" | "value";

/**
 * 結果表の並べ替え（列と向き）を BE の sort_by / sort_order に直す。
 *
 * 「値」の列は表示している種類で中身が変わるので、観測値だけのときは
 * observed_value、それ以外（予測値 / 両方）は point_estimate で並べる。
 */
export function toRecordSort(
  field: RecordSortField,
  order: "asc" | "desc",
  valueType: RecordValueType
): { sort_by: RecordSortBy; sort_order: "asc" | "desc" } {
  if (field === "identifierCode") {
    return { sort_by: "identifier_code", sort_order: order };
  }
  return {
    sort_by: valueType === "observed" ? "observed_value" : "point_estimate",
    sort_order: order,
  };
}
